import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import isToday from 'dayjs/plugin/isToday';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import calendar from 'dayjs/plugin/calendar';
import isoWeek from 'dayjs/plugin/isoWeek';

// locales
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/vi';
import 'dayjs/locale/th';
import 'dayjs/locale/id';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/pa-in';

dayjs.locale('en');

dayjs.extend(LocalizedFormat);
dayjs.extend(isToday);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(calendar);
dayjs.extend(isoWeek);

const REGISTERED_DAYJS_LOCALES = new Set([
  'en',
  'fr',
  'es',
  'vi',
  'th',
  'id',
  'zh-tw',
  'pa-in',
]);

const LAST_RESORT_DAYJS_LOCALE = 'en';

let officialLanguageFallback: string | null = null;

const mapToDayjs = (lang: string): string => {
  const lower = lang.toLowerCase();
  if (lower === 'zh-hant') return 'zh-tw';
  if (lower === 'pa') return 'pa-in';
  return lower;
};

const dayjsLocaleFor = (lang: string): string => {
  const primary = mapToDayjs(lang);
  if (REGISTERED_DAYJS_LOCALES.has(primary)) return primary;

  if (officialLanguageFallback) {
    const official = mapToDayjs(officialLanguageFallback);
    if (REGISTERED_DAYJS_LOCALES.has(official)) return official;
  }

  return LAST_RESORT_DAYJS_LOCALE;
};

export const setOfficialLanguageFallback = (lang: string | undefined): void => {
  officialLanguageFallback = lang ?? null;
  if (typeof window === 'undefined') return;
  import('i18next').then((module) => {
    dayjs.locale(dayjsLocaleFor(module.default.language));
  });
};

if (typeof window !== 'undefined') {
  import('i18next').then((module) => {
    const i18n = module.default;
    dayjs.locale(dayjsLocaleFor(i18n.language));

    i18n.on('languageChanged', (lng: string) => {
      dayjs.locale(dayjsLocaleFor(lng));
    });
  });
}

export default dayjs;
